import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, Info } from "lucide-react";
import { useStore } from "../lib/store";
import api from "../lib/api";
import { getTurnstileToken } from "../lib/turnstile";
import ShippingDisclaimer from "../components/ShippingDisclaimer";

const COUNTRIES = [
  "United States", "Canada", "Mexico", "Brazil", "Germany", "France", "Spain",
  "Italy", "Netherlands", "Belgium", "Ireland", "Austria", "Switzerland", "Norway",
  "Portugal", "Greece", "Philippines", "India", "UAE", "Saudi Arabia", "Other",
];
const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida",
  "Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine",
  "Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska",
  "Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota",
  "Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
  "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming",
];

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, subtotal, clearCart, user } = useStore();
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("bank");
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [quoting, setQuoting] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [couponError, setCouponError] = useState("");
  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    country: "United States",
    state: "",
    city: "",
    address: "",
    postal_code: "",
  });

  // Refresh DHL quote whenever cart or destination changes.
  useEffect(() => {
    if (cart.length === 0 || !form.country) { setQuote(null); return; }
    let cancelled = false;
    setQuoting(true);
    setQuoteError("");
    const items = cart.map((l) => ({ product_id: l.product_id, sku: l.sku, qty: l.qty }));
    api.post("/checkout/quote", { items, country: form.country, state: form.state, coupon_code: appliedCoupon || null })
      .then((r) => { if (!cancelled) setQuote(r.data); })
      .catch((e) => {
        if (!cancelled) {
          setQuote(null);
          const msg = e.response?.data?.detail || "Unable to calculate shipping";
          setQuoteError(msg);
          if (appliedCoupon) setCouponError(msg);
        }
      })
      .finally(() => { if (!cancelled) setQuoting(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, form.country, form.state, appliedCoupon]);

  if (cart.length === 0) {
    return (
      <div className="container-kv pt-32 pb-20 text-center" data-testid="checkout-empty">
        <h1 className="font-serif text-3xl mb-4">Your cart is empty</h1>
        <Link to="/shop" className="btn-gold inline-flex">Shop Products</Link>
      </div>
    );
  }

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const placeOrder = async () => {
    setSubmitting(true);
    try {
      const turnstile_token = await getTurnstileToken();
      const items = cart.map((l) => ({ product_id: l.product_id, sku: l.sku, qty: l.qty }));
      const origin = window.location.origin;
      const { data } = await api.post("/checkout/session", {
        items, address: form, origin_url: origin, payment_method: paymentMethod, coupon_code: appliedCoupon || null, turnstile_token,
      });
      if (paymentMethod === "stripe") {
        window.location.href = data.url;
      } else if (paymentMethod === "bank") {
        clearCart();
        navigate(`/payment/bank?order_id=${data.order_id}&t=${data.access_token}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setSubmitting(false);
    }
  };

  const steps = ["Shipping", "Payment", "Review"];
  const shipCost = quote?.shipping ?? 0;
  const tax = quote?.tax ?? 0;
  const discount = quote?.discount ?? 0;
  const total = quote?.total ?? subtotal;

  const applyCoupon = () => {
    setCouponError("");
    const code = couponCode.trim().toUpperCase();

    if (!code) {
      setAppliedCoupon("");
      setCouponError("Enter a promo code first.");
      return;
    }

    setAppliedCoupon(code);
  };

  const removeCoupon = () => {
    setCouponCode("");
    setAppliedCoupon("");
    setCouponError("");
  };
  const canContinueShip = form.full_name && form.email && form.address && form.city && form.country && !quoteError && !quoting && quote;

  return (
    <div className="container-kv pt-28 pb-20" data-testid="checkout-page">
      <h1 className="font-serif text-4xl mb-2 font-light">Checkout</h1>
      <div className="flex gap-4 mb-10 text-sm" data-testid="checkout-steps">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-3">
            <span className={`w-6 h-6 flex items-center justify-center text-xs border ${step === i + 1 ? "border-[#C8A97E] text-[#C8A97E]" : step > i + 1 ? "bg-[#C8A97E] text-black border-[#C8A97E]" : "border-white/15 text-[#75726D]"}`}>
              {i + 1}
            </span>
            <span className={step >= i + 1 ? "text-white" : "text-[#75726D]"}>{s}</span>
            {i < 2 && <span className="text-[#75726D]">·</span>}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-6">
          {step === 1 && (
            <div className="card-kv space-y-4" data-testid="step-shipping">
              <h2 className="font-serif text-2xl mb-4">Shipping details</h2>
              <ShippingDisclaimer compact />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name" name="full_name" value={form.full_name} onChange={onChange} />
                <Field label="Email" name="email" value={form.email} onChange={onChange} type="email" />
                <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
                <div>
                  <label className="text-xs text-[#75726D] uppercase tracking-widest mb-2 block">Country</label>
                  <select name="country" value={form.country} onChange={onChange} data-testid="country-select">
                    {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                {form.country === "United States" && (
                  <div>
                    <label className="text-xs text-[#75726D] uppercase tracking-widest mb-2 block">State</label>
                    <select name="state" value={form.state} onChange={onChange} data-testid="state-select">
                      <option value="">Select state</option>
                      {US_STATES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                )}
                <Field label="City" name="city" value={form.city} onChange={onChange} />
                <Field label="Address" name="address" value={form.address} onChange={onChange} />
                <Field label="Postal Code" name="postal_code" value={form.postal_code} onChange={onChange} />
              </div>

              {/* DHL quote surface */}
              <div className="mt-2 p-4 border border-white/10 bg-[#0A0E0A]" data-testid="dhl-quote">
                <div className="flex items-center gap-2 mb-2">
                  <Package size={16} className="text-[#C8A97E]" />
                  <span className="overline">DHL Express · from Borneo</span>
                </div>
                {quoting && <div className="text-sm text-[#A8A5A1]">Calculating DHL rate...</div>}
                {quoteError && <div className="text-sm text-[#FCA5A5]" data-testid="dhl-error">{quoteError}</div>}
                {!quoting && !quoteError && quote && (
                  <div className="text-sm text-[#A8A5A1]">
                    Worldwide Express · billable {quote.shipping_info?.billable_kg}kg · delivered 3–7 business days · <span className="text-[#F4F1ED]">${shipCost.toFixed(2)}</span>
                  </div>
                )}
              </div>

              <button
                onClick={() => setStep(2)}
                className="btn-gold mt-4"
                disabled={!canContinueShip}
                data-testid="step-ship-next"
              >
                Continue to Payment
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="card-kv space-y-4" data-testid="step-payment">
              <h2 className="font-serif text-2xl mb-4">Payment method</h2>
              <PayOption id="stripe" label="Credit / Debit Card" sub="Secure checkout via Stripe" paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} />
              <PayOption id="paypal" label="PayPal" sub="Coming soon — currently disabled" paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} disabled />
              <PayOption id="bank" label="Bank Transfer" sub="Manual verification · 1–2 business days" paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} />

              <div className="flex gap-3 pt-4">
                <button onClick={() => setStep(1)} className="btn-ghost" data-testid="step-pay-back">Back</button>
                <button onClick={() => setStep(3)} className="btn-gold flex-1" data-testid="step-pay-next">Review Order</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="card-kv space-y-6" data-testid="step-review">
              <h2 className="font-serif text-2xl">Review your order</h2>
              <div>
                <div className="overline mb-2">Ship to</div>
                <div className="text-sm text-[#A8A5A1]">
                  {form.full_name} · {form.email}<br />
                  {form.address}, {form.city}, {form.state && `${form.state}, `}{form.country} {form.postal_code}
                </div>
              </div>
              <div>
                <div className="overline mb-2">Carrier</div>
                <div className="text-sm text-[#A8A5A1]">DHL Express Worldwide · from Borneo</div>
              </div>
              <div>
                <div className="overline mb-2">Payment</div>
                <div className="text-sm text-[#A8A5A1] capitalize">{paymentMethod === "stripe" ? "Credit Card (Stripe)" : paymentMethod}</div>
              </div>

              <div className="flex gap-3 p-4 border border-[#5C1D1D] bg-[#2A0E0E] text-[#FCA5A5] text-xs leading-relaxed" data-testid="duty-notice">
                <Info size={16} className="shrink-0 mt-0.5" />
                <div>
                  <strong>Import duties &amp; taxes are your responsibility.</strong> Your total below covers products and DHL Express shipping only. Any customs duties, VAT, or local taxes will be collected by your country's customs on arrival — they are not prepaid.
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="btn-ghost" data-testid="step-review-back">Back</button>
                <button
                  onClick={placeOrder}
                  disabled={submitting || !quote}
                  className="btn-gold flex-1"
                  data-testid="place-order-btn"
                >
                  {submitting ? "Processing..." : `Place Order · $${total.toFixed(2)}`}
                </button>
              </div>
            </div>
          )}
        </div>

        <aside className="card-kv h-fit sticky top-24" data-testid="checkout-summary">
          <h3 className="font-serif text-xl mb-4">Order summary</h3>
          <div className="space-y-3 mb-5 max-h-60 overflow-y-auto">
            {cart.map((l) => (
              <div key={l.sku} className="flex gap-3 text-sm">
                <img src={l.image} alt="" className="w-12 h-12 object-cover" loading="lazy" />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{l.name}</div>
                  <div className="text-xs text-[#75726D]">{l.weight} × {l.qty}</div>
                </div>
                <div>${(l.price * l.qty).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/5 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#A8A5A1]">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>

            <div className="py-3 border-y border-white/5 space-y-2">
              <div className="text-[#A8A5A1] text-xs uppercase tracking-widest">Promo Code</div>
              <div className="flex gap-2">
                <input
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="SALE10"
                  className="flex-1"
                />
                {appliedCoupon ? (
                  <button type="button" onClick={removeCoupon} className="btn-ghost px-3">Remove</button>
                ) : (
                  <button type="button" onClick={applyCoupon} className="btn-gold px-3">Apply</button>
                )}
              </div>

              {appliedCoupon && quote?.promo && (
                <div className="text-xs text-[#C8A97E]">Applied: {quote.promo.code} — {quote.promo.title}</div>
              )}

              {couponError && <div className="text-xs text-[#FCA5A5]">{couponError}</div>}
            </div>

            {discount > 0 && (
              <div className="flex justify-between text-[#C8A97E]">
                <span>Discount</span>
                <span>-${discount.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-[#A8A5A1]">DHL Shipping</span>
              <span data-testid="summary-shipping">
                {quoting ? "—" : quoteError ? "—" : quote ? `$${shipCost.toFixed(2)}` : "—"}
              </span>
            </div>
            <div className="flex justify-between"><span className="text-[#A8A5A1]">Tax</span><span>${tax.toFixed(2)}</span></div>
            <div className="flex justify-between pt-3 border-t border-white/5 font-medium">
              <span>Total</span><span data-testid="summary-total">{quote ? `$${total.toFixed(2)}` : "—"}</span>
            </div>
            <p className="text-[11px] text-[#75726D] pt-2 leading-relaxed">
              Duties &amp; import taxes are the recipient's responsibility.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, ...p }) {
  return (
    <div>
      <label className="text-xs text-[#75726D] uppercase tracking-widest mb-2 block">{label}</label>
      <input {...p} data-testid={`field-${p.name}`} />
    </div>
  );
}

function PayOption({ id, label, sub, paymentMethod, setPaymentMethod, disabled }) {
  const active = paymentMethod === id;
  return (
    <label
      className={`block p-4 border cursor-pointer transition-colors ${active ? "border-[#C8A97E]" : "border-white/10 hover:border-white/20"} ${disabled ? "opacity-40 pointer-events-none" : ""}`}
      data-testid={`pay-option-${id}`}
    >
      <input type="radio" name="payment" checked={active} onChange={() => !disabled && setPaymentMethod(id)} className="sr-only" />
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">{label}</div>
          <div className="text-xs text-[#75726D] mt-0.5">{sub}</div>
        </div>
        <span className={`w-4 h-4 rounded-full border-2 ${active ? "border-[#C8A97E] bg-[#C8A97E]" : "border-white/30"}`} />
      </div>
    </label>
  );
}
